import { useState, useEffect } from 'react';
import Sidebar from '../components/Sidebar';
import DashboardHeader from '../components/DashboardHeader';
import AddActivityModal from './components/AddActivityModal';
import EditActivityModal from './components/EditActivityModal';
import ActivityList from './components/ActivityList';
import ActivityCalendar from './components/ActivityCalendar';

export interface FarmingActivity {
  id: string;
  activityName: string;
  activityType: string;
  cropType: string;
  location: string;
  assignedTo: string;
  scheduledDate: string;
  completionDate?: string;
  status: 'Scheduled' | 'In Progress' | 'Completed' | 'Delayed';
  priority: 'Low' | 'Medium' | 'High' | 'Critical';
  description: string;
  resources: string;
  estimatedCost: number;
  actualCost?: number;
  notes?: string;
  createdDate: string;
}

export default function FarmingActivities() {
  const [activities, setActivities] = useState<FarmingActivity[]>([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingActivity, setEditingActivity] = useState<FarmingActivity | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('All');
  const [filterType, setFilterType] = useState('All');
  const [viewMode, setViewMode] = useState<'list' | 'calendar'>('list');

  useEffect(() => {
    const savedActivities = localStorage.getItem('farmingActivities');
    if (savedActivities) {
      setActivities(JSON.parse(savedActivities));
    } else {
      const sampleActivities: FarmingActivity[] = [
        {
          id: '1',
          activityName: 'Moringa Seed Planting',
          activityType: 'Planting',
          cropType: 'Moringa',
          location: 'Field A - North Section',
          assignedTo: 'Kalyan',
          scheduledDate: '2024-02-01',
          status: 'Completed',
          priority: 'High',
          description: 'Plant moringa seeds in prepared beds with proper spacing',
          resources: 'Seeds (5kg), Fertilizer, Water pump, 4 workers',
          estimatedCost: 15000,
          actualCost: 14500,
          completionDate: '2024-02-01',
          notes: 'Completed successfully with good weather conditions',
          createdDate: '2024-01-25'
        },
        {
          id: '2',
          activityName: 'Irrigation System Maintenance',
          activityType: 'Maintenance',
          cropType: 'All Crops',
          location: 'Main Irrigation Hub',
          assignedTo: 'Amol',
          scheduledDate: '2024-02-05',
          status: 'In Progress',
          priority: 'Critical',
          description: 'Check and repair irrigation pipes, clean filters, test pumps',
          resources: 'Pipe fittings, Tools, 2 technicians',
          estimatedCost: 8000,
          notes: 'Found 3 leaks in sector B, repairing now',
          createdDate: '2024-01-28'
        },
        {
          id: '3',
          activityName: 'Organic Fertilizer Application',
          activityType: 'Fertilization',
          cropType: 'Moringa',
          location: 'Field B - South Section',
          assignedTo: 'Shirish',
          scheduledDate: '2024-02-08',
          status: 'Scheduled',
          priority: 'Medium',
          description: 'Apply organic compost and bio-fertilizer to moringa plants',
          resources: 'Organic compost (500kg), Bio-fertilizer, Spreader, 3 workers',
          estimatedCost: 12000,
          createdDate: '2024-01-30'
        },
        {
          id: '4',
          activityName: 'Pest Control Inspection',
          activityType: 'Pest Control',
          cropType: 'Moringa',
          location: 'All Fields',
          assignedTo: 'Kalyan',
          scheduledDate: '2024-02-10',
          status: 'Scheduled',
          priority: 'High',
          description: 'Inspect all moringa plants for pests and diseases, apply organic pesticides if needed',
          resources: 'Neem oil spray, Inspection tools, 2 workers',
          estimatedCost: 6000,
          createdDate: '2024-02-01'
        },
        {
          id: '5',
          activityName: 'Moringa Leaf Harvesting',
          activityType: 'Harvesting',
          cropType: 'Moringa',
          location: 'Field A - North Section',
          assignedTo: 'Amol',
          scheduledDate: '2024-02-15',
          status: 'Scheduled',
          priority: 'Critical',
          description: 'Harvest mature moringa leaves for processing',
          resources: 'Harvesting baskets, Gloves, 6 workers, Transport vehicle',
          estimatedCost: 20000,
          createdDate: '2024-02-02'
        },
        {
          id: '6',
          activityName: 'Soil Testing and Analysis',
          activityType: 'Soil Management',
          cropType: 'All Crops',
          location: 'Field C - East Section',
          assignedTo: 'Shirish',
          scheduledDate: '2024-02-12',
          status: 'Scheduled',
          priority: 'Medium',
          description: 'Collect soil samples and send for laboratory analysis',
          resources: 'Soil testing kit, Sample containers, Lab fees',
          estimatedCost: 5000,
          createdDate: '2024-02-03'
        },
        {
          id: '7',
          activityName: 'Weeding and Land Preparation',
          activityType: 'Land Preparation',
          cropType: 'Moringa',
          location: 'Field D - West Section',
          assignedTo: 'Kalyan',
          scheduledDate: '2024-01-28',
          completionDate: '2024-01-29',
          status: 'Completed',
          priority: 'Medium',
          description: 'Remove weeds and prepare land for new planting',
          resources: 'Weeding tools, Tractor, 5 workers',
          estimatedCost: 10000,
          actualCost: 9500,
          notes: 'Land ready for planting next week',
          createdDate: '2024-01-20'
        },
        {
          id: '8',
          activityName: 'Drip Irrigation Installation',
          activityType: 'Irrigation',
          cropType: 'Moringa',
          location: 'Field B - South Section',
          assignedTo: 'Amol',
          scheduledDate: '2024-02-06',
          status: 'In Progress',
          priority: 'High',
          description: 'Install new drip irrigation system for water efficiency',
          resources: 'Drip pipes, Emitters, Connectors, 3 technicians',
          estimatedCost: 35000,
          notes: '60% installation completed',
          createdDate: '2024-01-29'
        }
      ];
      setActivities(sampleActivities);
      localStorage.setItem('farmingActivities', JSON.stringify(sampleActivities));
    }
  }, []);

  const handleAddActivity = (activity: Omit<FarmingActivity, 'id' | 'createdDate'>) => {
    const newActivity = {
      ...activity,
      id: Date.now().toString(),
      createdDate: new Date().toISOString().split('T')[0]
    };
    const updatedActivities = [newActivity, ...activities];
    setActivities(updatedActivities);
    localStorage.setItem('farmingActivities', JSON.stringify(updatedActivities));
    setShowAddModal(false);
    window.dispatchEvent(new Event('farmingActivitiesUpdated'));
  };

  const handleEditActivity = (updatedActivity: FarmingActivity) => {
    const updatedActivities = activities.map(act => 
      act.id === updatedActivity.id ? updatedActivity : act
    );
    setActivities(updatedActivities);
    localStorage.setItem('farmingActivities', JSON.stringify(updatedActivities));
    setEditingActivity(null);
    window.dispatchEvent(new Event('farmingActivitiesUpdated'));
  };

  const handleDeleteActivity = (id: string) => {
    if (confirm('Are you sure you want to delete this farming activity?')) {
      const updatedActivities = activities.filter(act => act.id !== id);
      setActivities(updatedActivities);
      localStorage.setItem('farmingActivities', JSON.stringify(updatedActivities));
      window.dispatchEvent(new Event('farmingActivitiesUpdated'));
    }
  };

  const handleStatusChange = (id: string, newStatus: FarmingActivity['status']) => {
    const updatedActivities = activities.map(act => {
      if (act.id === id) {
        const updated = { ...act, status: newStatus };
        if (newStatus === 'Completed' && !act.completionDate) {
          updated.completionDate = new Date().toISOString().split('T')[0];
        }
        return updated;
      }
      return act;
    });
    setActivities(updatedActivities);
    localStorage.setItem('farmingActivities', JSON.stringify(updatedActivities));
    window.dispatchEvent(new Event('farmingActivitiesUpdated'));
  };

  const filteredActivities = activities.filter(activity => {
    const matchesSearch = activity.activityName.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         activity.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         activity.cropType.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         activity.location.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = filterStatus === 'All' || activity.status === filterStatus;
    const matchesType = filterType === 'All' || activity.activityType === filterType;
    return matchesSearch && matchesStatus && matchesType;
  });

  const totalActivities = activities.length;
  const scheduledActivities = activities.filter(a => a.status === 'Scheduled').length;
  const inProgressActivities = activities.filter(a => a.status === 'In Progress').length;
  const completedActivities = activities.filter(a => a.status === 'Completed').length;
  const totalEstimatedCost = activities.reduce((sum, a) => sum + a.estimatedCost, 0);
  const totalActualCost = activities.reduce((sum, a) => sum + (a.actualCost || 0), 0);

  return (
    <div className="flex min-h-screen bg-gray-50">
      <Sidebar />
      <div className="flex-1 flex flex-col">
        <DashboardHeader />
        <main className="flex-1 p-8">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h1 className="text-3xl font-bold text-gray-900">Farming Activities</h1>
                <p className="text-sm text-gray-600 mt-1">Manage and track all farming operations</p>
              </div>
              <button
                onClick={() => setShowAddModal(true)}
                className="px-6 py-3 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors font-medium flex items-center gap-2 cursor-pointer whitespace-nowrap"
              >
                <i className="ri-add-line text-xl w-5 h-5 flex items-center justify-center"></i>
                Add Activity
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
              <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Total Activities</p>
                    <p className="text-2xl font-bold text-gray-900">{totalActivities}</p>
                  </div>
                  <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center">
                    <i className="ri-plant-line text-2xl text-teal-600 w-6 h-6 flex items-center justify-center"></i>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Scheduled</p>
                    <p className="text-2xl font-bold text-blue-600">{scheduledActivities}</p>
                  </div>
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                    <i className="ri-calendar-line text-2xl text-blue-600 w-6 h-6 flex items-center justify-center"></i>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600 mb-1">In Progress</p>
                    <p className="text-2xl font-bold text-orange-600">{inProgressActivities}</p>
                  </div>
                  <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
                    <i className="ri-time-line text-2xl text-orange-600 w-6 h-6 flex items-center justify-center"></i>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Completed</p>
                    <p className="text-2xl font-bold text-green-600">{completedActivities}</p>
                  </div>
                  <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                    <i className="ri-checkbox-circle-line text-2xl text-green-600 w-6 h-6 flex items-center justify-center"></i>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Total Cost</p>
                    <p className="text-2xl font-bold text-gray-900">₹{totalActualCost > 0 ? totalActualCost.toLocaleString() : totalEstimatedCost.toLocaleString()}</p>
                  </div>
                  <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center">
                    <i className="ri-money-rupee-circle-line text-2xl text-amber-600 w-6 h-6 flex items-center justify-center"></i>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 mb-6">
              <div className="flex flex-col lg:flex-row gap-4 items-center">
                <div className="flex-1 relative w-full">
                  <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-lg w-5 h-5 flex items-center justify-center"></i>
                  <input
                    type="text"
                    placeholder="Search activities by name, crop, location..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-12 pr-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                  />
                </div>
                <select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  className="px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm cursor-pointer"
                >
                  <option value="All">All Status</option>
                  <option value="Scheduled">Scheduled</option>
                  <option value="In Progress">In Progress</option>
                  <option value="Completed">Completed</option>
                  <option value="Delayed">Delayed</option>
                </select>
                <select
                  value={filterType}
                  onChange={(e) => setFilterType(e.target.value)}
                  className="px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm cursor-pointer"
                >
                  <option value="All">All Types</option>
                  <option value="Planting">Planting</option>
                  <option value="Harvesting">Harvesting</option>
                  <option value="Irrigation">Irrigation</option>
                  <option value="Fertilization">Fertilization</option>
                  <option value="Pest Control">Pest Control</option>
                  <option value="Soil Management">Soil Management</option>
                  <option value="Maintenance">Maintenance</option>
                  <option value="Land Preparation">Land Preparation</option>
                </select>
                <div className="flex gap-2">
                  <button
                    onClick={() => setViewMode('list')}
                    className={`px-4 py-3 rounded-lg transition-colors cursor-pointer whitespace-nowrap ${
                      viewMode === 'list' ? 'bg-teal-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    <i className="ri-list-check text-lg w-5 h-5 flex items-center justify-center"></i>
                  </button>
                  <button
                    onClick={() => setViewMode('calendar')}
                    className={`px-4 py-3 rounded-lg transition-colors cursor-pointer whitespace-nowrap ${
                      viewMode === 'calendar' ? 'bg-teal-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    <i className="ri-calendar-2-line text-lg w-5 h-5 flex items-center justify-center"></i>
                  </button>
                </div>
              </div>
            </div>

            {viewMode === 'list' ? (
              <ActivityList
                activities={filteredActivities}
                onEdit={setEditingActivity}
                onDelete={handleDeleteActivity}
                onStatusChange={handleStatusChange}
              />
            ) : (
              <ActivityCalendar
                activities={filteredActivities}
                onEdit={setEditingActivity}
              />
            )}
          </div>
        </main>
      </div>

      {showAddModal && (
        <AddActivityModal
          onClose={() => setShowAddModal(false)}
          onAdd={handleAddActivity}
        />
      )}

      {editingActivity && (
        <EditActivityModal
          activity={editingActivity}
          onClose={() => setEditingActivity(null)}
          onSave={handleEditActivity}
        />
      )}
    </div>
  );
}
